import { Carousel } from 'react-responsive-carousel'
import ReactPlayerImport from 'react-player/lazy'

// CJS/ESM interop: some Vite/Rollup build configs resolve the default
// export as { default: Component } instead of Component directly.
const ReactPlayer =
  typeof ReactPlayerImport === 'object' &&
  ReactPlayerImport !== null &&
  'default' in ReactPlayerImport
    ? (ReactPlayerImport as any).default
    : ReactPlayerImport
import React, { CSSProperties } from 'react'
import useCollectionTemplate from 'hooks/useCollectionTemplate'
import useTeams from 'hooks/useTeams'
import { Moment, NFT } from 'types'
import play from '/assets/Icon/Play.svg'
import pause from '/assets/Icon/Pause.svg'
import mute from '/assets/Icon/Mute.svg'
import sound from '/assets/Icon/Sound.svg'

const MomentSlide = ({
  id,
  url,
  isSelected,
  isPlaying,
  isMuted
}: {
  id: string
  url: string
  isSelected?: boolean
  isPlaying?: boolean
  isMuted?: boolean
}) => {
  const [isLoading, setIsLoading] = React.useState(true)
  const playerRef = React.useRef<InstanceType<typeof ReactPlayer>>(null)

  React.useEffect(() => {
    if (!isSelected && isPlaying) {
      playerRef.current?.seekTo(0)
    }
  }, [isSelected, isPlaying])

  return (
    <div className="relative mx-auto aspect-square px-4 md:aspect-video">
      {isLoading && (
        <div className="absolute left-1/2 top-0 flex h-full w-[97%] translate-x-[-50%] animate-pulse items-center justify-center bg-black"></div>
      )}
      <ReactPlayer
        ref={playerRef}
        onReady={() => setIsLoading(false)}
        width="100%"
        height="100%"
        url={url}
        loop={true}
        muted={isMuted}
        playing={isSelected && isPlaying}
        playsinline={true}
      />
    </div>
  )
}

function HomeCarousel({
  title,
  collectionName = 'AllButRookies',
  selectedTournament = 'AFL'
}: {
  title?: string
  collectionName?: string
  selectedTournament?: string
}) {
  const isCollectionMintMoments = collectionName?.includes('mint-moments')
  const { momentTemplates } = useCollectionTemplate(
    isCollectionMintMoments ? 'Mint Moments 2025' : collectionName
  )

  const [randomMoments, setRandomMoments] = React.useState<NFT<Moment>[]>([])
  const { teams } = useTeams()
  const [selectedTeam, setSelectedTeam] = React.useState('')
  const [isPlaying, setIsPlaying] = React.useState(true)
  const [isMuted, setIsMuted] = React.useState(true)
  const [isHovering, setIsHovering] = React.useState(false)
  const slogan = teams.find((team) => team.teamName === selectedTeam)?.slogan
  const isMobile = window.innerWidth < 768
  const centerSlidePercentage = window.innerWidth < 768 ? 100 : 50
  const slug = window.location.pathname.split('/')[2]

  const arrowStyles: CSSProperties = {
    position: 'absolute',
    zIndex: 2,
    top: '0',
    width: '22%',
    height: '96%',
    cursor: 'pointer'
  }
  const indicatorStyles: CSSProperties = {
    background: '#fff',
    width: 24,
    height: 5,
    opacity: 0.5,
    display: 'inline-block',
    margin: '0 4px'
  }

  const momentArray: any = React.useMemo(() => {
    return momentTemplates.map((moment) => {
      return {
        ...moment.templateData.immutableData,
        id: moment.id,
        templateId: moment.templateData.templateId
      }
    })
  }, [momentTemplates.length, selectedTeam])

  const filteredMoments = momentArray?.filter(
    // filter by team if filterByTeam is passed
    (moment: any) =>
      selectedTeam === 'AFL'
        ? moment.competition === 'AFL'
        : selectedTeam === 'AFLW'
        ? moment.competition === 'AFLW'
        : moment.teamOfInvolvedPlayer === selectedTeam
  )

  React.useEffect(() => {
    setSelectedTeam(selectedTournament)
  }, [slug])

  React.useEffect(() => {
    if (momentTemplates.length > 0 && filteredMoments.length > 0) {
      const allMomentTemplates = filteredMoments.filter(
        (moment: { playersJumperNumber: undefined }) =>
          moment.playersJumperNumber !== undefined ? true : false
      )
      const _randomMoments = []
      const usedIndexes: number[] = []

      while (_randomMoments.length < 6) {
        const randomIndex = Math.floor(
          Math.random() * allMomentTemplates.length
        )

        if (usedIndexes.includes(randomIndex)) {
          continue
        }

        _randomMoments.push(allMomentTemplates[randomIndex])
        usedIndexes.push(randomIndex)
      }

      setRandomMoments(_randomMoments)
    }
  }, [momentTemplates.length, filteredMoments.length, selectedTeam])

  const customRenderItem = (item: any, props: any) => (
    <item.type {...item.props} {...props} />
  )

  return (
    <div className="relative mx-auto max-w-[1900px] bg-gradient-to-b from-[#100F14] to-[#1E1B2E] pb-10 xl:pb-16">
      <div className="flex w-full flex-col items-center py-6 md:pt-10 xl:pt-16">
        <p className="z-10 text-center font-podium49 text-3xl uppercase !leading-[100%] lg:text-[56px] xl:text-[64px]">
          {title ? title : `Shop ${slogan} best moments`}
        </p>
        {!title && (
          <div className="flex w-screen flex-nowrap items-center justify-between gap-x-2 overflow-x-auto p-3 md:justify-center">
            {teams.map((team) => (
              <button
                key={team.id}
                className={`flex min-w-[36px] cursor-pointer items-center justify-center ${
                  team.teamName === selectedTeam
                    ? 'scale-105 border-b-[3px] border-[#3F76EE]'
                    : ''
                }`}
                onClick={() => setSelectedTeam(team.teamName)}
              >
                <img src={team.icon} className="w-10" />
              </button>
            ))}
          </div>
        )}
      </div>
      {randomMoments.length > 0 ? (
        <Carousel
          centerSlidePercentage={centerSlidePercentage}
          centerMode={true}
          swipeable={true}
          infiniteLoop={true}
          showStatus={false}
          showThumbs={false}
          renderItem={customRenderItem}
          renderArrowPrev={(onClickHandler) => (
            <button
              type="button"
              onClick={onClickHandler}
              style={{ ...arrowStyles, left: 15 }}
            ></button>
          )}
          renderArrowNext={(onClickHandler) => (
            <button
              type="button"
              onClick={onClickHandler}
              style={{ ...arrowStyles, right: 15 }}
            ></button>
          )}
          renderIndicator={(onClickHandler, isSelected, index, label) => {
            if (isSelected) {
              return (
                <li
                  style={{ ...indicatorStyles, opacity: '1' }}
                  aria-label={`Selected: AFL Moment ${index + 1}`}
                  title={`Selected: AFL Moment ${index + 1}`}
                />
              )
            }
            return (
              <li
                style={indicatorStyles}
                onClick={onClickHandler}
                onKeyDown={onClickHandler}
                value={index}
                key={index}
                role="button"
                tabIndex={0}
                title={`AFL Moment ${index + 1}`}
                aria-label={`AFL Moment ${index + 1}`}
              />
            )
          }}
        >
          {randomMoments?.map((moment: any, i: number) => (
            <MomentSlide
              id={moment.id}
              key={i}
              url={moment?.video.replaceAll('%20', '%2520')}
              isPlaying={isPlaying}
              isMuted={isMuted}
            />
          ))}
        </Carousel>
      ) : (
        <div className="flex h-[80vh] w-full items-center justify-center">
          <p className="text-2xl text-white">No moments to display</p>
        </div>
      )}

      <div className="pointer-events-none absolute bottom-0 z-10 flex w-full justify-center pb-7 xl:pb-14">
        <div
          className={`flex justify-between px-4 ${
            isMobile ? 'w-full' : 'w-1/2'
          }`}
        >
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="pointer-events-auto"
          >
            <img className="size-8" src={!isPlaying ? play : pause}></img>
          </button>
          <button
            onClick={() => setIsMuted(!isMuted)}
            className="pointer-events-auto"
          >
            <img className="size-8" src={isMuted ? mute : sound}></img>
          </button>
        </div>
      </div>
    </div>
  )
}

export default HomeCarousel
